// Thin wrapper over the SDK's NATIVE auth module (packages/sdk/src/auth.ts) —
// kept so the funnel's call sites stay stable. Everything here is hosted-UI-
// free: plain Cognito JSON API calls; tokens are persisted where the SDK's
// OIDC client reads them, so a fresh connect() hydrates the session.

import { AuthError, createAuth, persistOidcSession } from "/sdk/index.js";
import { s3pProject } from "/__s3p/project.js";

const clientId = s3pProject.cognito.clientId;

export interface OidcTokens {
	id_token: string;
	access_token: string;
	refresh_token?: string;
	token_type: string;
	expires_in?: number;
}

/** Legacy alias — funnel code catches CognitoError and branches on `.code`. */
export { AuthError as CognitoError };

const auth = createAuth({
	region: s3pProject.aws.region,
	clientId,
	// Persist where the SDK's OIDC client reads, via the SDK's own helper, so
	// the funnel's follow-up connect() adopts the session (no hardcoded key).
	onSession: (session) => {
		persistOidcSession(clientId, session);
	},
});

export async function signUp(email: string, password: string): Promise<{ confirmed: boolean }> {
	return auth.signUp(email, password);
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
	await auth.confirmSignUp(email, code);
}

export async function resendCode(email: string): Promise<void> {
	await auth.resendCode(email);
}

/** Sign in with email + password. Tokens are persisted by onSession; the
 *  return value is informational. Challenge-requiring accounts (temp
 *  passwords, MFA) should use the /signin page, which renders the rounds. */
export async function passwordSignIn(email: string, password: string): Promise<OidcTokens> {
	const r = await auth.signIn(email, password);
	if (r.status !== "ok") {
		throw new AuthError(
			"ChallengeRequired",
			`sign-in needs another step (${r.challenge}) — use the sign-in page`,
		);
	}
	return r.session as OidcTokens;
}

/** Kept for call-site compatibility: onSession already persisted the tokens;
 *  re-writing them is a harmless no-op-shaped idempotent write. */
export function persistSession(tokens: OidcTokens): void {
	// Cognito's native-auth response may omit expires_in; the OIDC session
	// shape requires it (the SDK uses it only for proactive refresh timing).
	persistOidcSession(clientId, { ...tokens, expires_in: tokens.expires_in ?? 3600 });
}

/** Adopt an agent code (a refresh token) as a browser session.
 *
 *  This is the whole of the auto-login handoff: the agent already holds a
 *  refresh token, and a refresh token is all Cognito needs to mint a live
 *  session. So the agent can hand the human a URL that lands them signed in —
 *  no password typed, ever. `refresh()` persists via onSession, exactly like a
 *  password sign-in would. */
export async function adoptAgentCode(code: string): Promise<void> {
	await auth.refresh(code);
}
